import { podiumColor } from '@/lib/podium'

/**
 * Presentation of a player title (see doc/player_titles.md): the colour of its chip and its icon.
 *
 * The mapping lives here and not in a component because a title shows up in more than one place —
 * the badge next to the name today, the list on the profile later — and the two must not drift apart.
 *
 * Every title has a colour of its own, and none of them is a podium metal: a title is not a placing,
 * and gold, silver and bronze already mean "first, second, third" everywhere else. The only
 * exception is the ranking, which *is* a placing: ranking_1/2/3 take the podium colours instead of
 * inventing a second copy of them.
 */
export const TITLE_STYLES = {
  records_champion:             { color: 'deep-orange',        icon: 'mdi-trophy-variant' },
  track_record_holder:          { color: 'green-darken-1',     icon: 'mdi-flag-checkered' },

  ranking_1:                    { color: podiumColor(1),       icon: 'mdi-podium-gold' },
  ranking_2:                    { color: podiumColor(2),       icon: 'mdi-podium-silver' },
  ranking_3:                    { color: podiumColor(3),       icon: 'mdi-podium-bronze' },
  ranking_top_10:               { color: 'blue-grey-darken-2', icon: 'mdi-star-outline' },

  // The two variants of a discipline share the look: they are the same title (see the doc).
  champion_best_lap:            { color: 'cyan',               icon: 'mdi-timer-outline' },
  champion_best_lap_points:     { color: 'cyan',               icon: 'mdi-timer-outline' },
  champion_best_session:        { color: 'indigo',             icon: 'mdi-timer-sand' },
  champion_best_session_points: { color: 'indigo',             icon: 'mdi-timer-sand' },
  champion_stages:              { color: 'teal',               icon: 'mdi-map-marker-path' },
  champion_monthly_season:      { color: 'deep-purple',        icon: 'mdi-crown' },
}

/** A title that is not listed — a new one, or a key from a newer server — still gets a badge. */
const FALLBACK_STYLE = { color: 'grey', icon: 'mdi-certificate-outline' }

/** Colour and icon of one title; see FALLBACK_STYLE for anything not listed. */
export function titleStyle(key)
{
  return TITLE_STYLES[key] ?? FALLBACK_STYLE
}

/**
 * The page a title is about: the records have their own leaderboard, the ranking has the standings.
 */
const TITLE_LINKS = {
  records_champion: { name: 'leaderboard' },
  track_record_holder: { name: 'leaderboard' },
  ranking_1: { name: 'standings' },
  ranking_2: { name: 'standings' },
  ranking_3: { name: 'standings' },
  ranking_top_10: { name: 'standings' },
}

/**
 * Where the badge leads, or null when the title has no page of its own.
 *
 * A champion opens the event it won — the one thing that is not a page of titles, so it comes with
 * the title itself (`meta`, built by the server).
 */
export function titleTo(key, meta = null)
{
  if (meta?.eventId) {
    return { name: 'event_detail', params: { id: meta.eventId } }
  }

  return TITLE_LINKS[key] ?? null
}
